// src/components/SHORTSTAYS/MEDIA/ShortMedia.tsx
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { RootState, AppDispatch } from "../../../../redux/store";
import {
  listenToShortstayData,
  upsertShortMedia,
  deleteMedia,
} from "../../../../redux/shortstaysSlice";

const darkGold = "#c6a55c";
const softGold = "#e5c87c";
const black = "#0a0a0a";
const borderGold = "1px solid rgba(198,165,92,0.4)";

const ShortMedia: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { listingId } = useParams<{ listingId: string }>();
  const current = useSelector((state: RootState) => state.shortstays.currentListing);

  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoPreview, setVideoPreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    if (current) {
      setPhotoUrls(current.photos || []);
      setVideoUrl(current.videoUrl || "");
    }
  }, [current]);

  const handleAddPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files);
    if (photoUrls.length + photos.length + selected.length > 50) {
      setError("⚠️ You can upload up to 50 photos.");
      return;
    }
    setPhotos((prev) => [...prev, ...selected]);
    setError(null);
  };

  const handleRemovePhoto = async (url: string) => {
    try {
if (!listingId) return;
await dispatch(deleteMedia({ listingId, photoUrl: url })).unwrap();
      setPhotoUrls((prev) => prev.filter((p) => p !== url));
    } catch {
      setError("❌ Failed to remove photo.");
    }
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const validateVideoDuration = (file: File): Promise<boolean> =>
    new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => resolve(video.duration <= 60);
      video.src = URL.createObjectURL(file);
    });

  const handleUpload = async () => {
    if (!listingId) return;
    setIsUploading(true);
    setError(null);
    setSuccess(false);

    try {
      if (photos.length) {
        await Promise.all(
          photos.map(async (file) => {
            await dispatch(
              upsertShortMedia({ files: [file], listingId, type: "photo" })
            ).unwrap();
          })
        );
      }

      if (videoFile) {
        const valid = await validateVideoDuration(videoFile);
        if (!valid) {
          setError("🚫 Video must be 60 seconds or less.");
          setIsUploading(false);
          return;
        }
        await dispatch(
          upsertShortMedia({ files: [videoFile], listingId, type: "video" })
        ).unwrap();
      }

      await dispatch(listenToShortstayData(listingId));
      setPhotos([]);
      setVideoFile(null);
      setVideoPreview("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error(err);
      setError("❌ Upload failed. Try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={styles.section}>
      {(isUploading || success || error) && (
        <div style={styles.overlay}>
          {isUploading && <CircularProgress size={64} style={{ color: softGold }} />}
          {success && (
            <div style={styles.successBox}>
              <CheckCircleOutlineIcon style={{ fontSize: 80, color: softGold }} />
              <p>Upload complete!</p>
            </div>
          )}
          {error && (
            <div style={styles.errorBox}>
              <ErrorOutlineIcon style={{ fontSize: 60, color: "#ff5555" }} />
              <p>{error}</p>
            </div>
          )}
        </div>
      )}

      <h3 style={styles.heading}>Shortstay Media</h3>
      <p style={styles.tip}>
        Upload up to 10 photos and one short video (max 60s) to showcase your space.
      </p>

      <div style={styles.mediaRow}>
        {/* Photos Section */}
        <div style={styles.card}>
          <h4 style={styles.subheading}>📸 Photos</h4>
          <div
            style={styles.uploadBox}
            onClick={() => photoInputRef.current?.click()}
          >
            <span style={{ fontSize: 48 }}>📷</span>
            <p>Add Photos</p>
          </div>
          <input
            ref={photoInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleAddPhotos}
            style={{ display: "none" }}
          />

          <div style={styles.gallery}>
            {photoUrls.map((url, i) => (
              <div key={i} style={styles.thumbWrapper}>
                <img src={url} alt={`photo-${i}`} style={styles.thumb} />
                <span
                  style={styles.trash}
                  onClick={() => handleRemovePhoto(url)}
                >
                  🗑️
                </span>
              </div>
            ))}
            {photos.map((file, i) => (
              <div key={i} style={styles.thumbWrapper}>
                <img
                  src={URL.createObjectURL(file)}
                  alt={`new-${i}`}
                  style={styles.thumb}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Video Section */}
        <div style={styles.card}>
          <h4 style={styles.subheading}>🎥 Intro Video</h4>
          {!videoPreview && !videoUrl ? (
            <div
              style={styles.uploadBox}
              onClick={() => videoInputRef.current?.click()}
            >
              <span style={{ fontSize: 48 }}>🎞️</span>
              <p>Add Video</p>
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              <video
                src={videoPreview || videoUrl}
                controls
                style={styles.video}
              />
              <button
                onClick={() => videoInputRef.current?.click()}
                style={styles.replaceBtn}
              >
                Replace Video
              </button>
            </div>
          )}
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoSelect}
            style={{ display: "none" }}
          />
        </div>
      </div>

      <button
        onClick={handleUpload}
        style={styles.uploadBtn}
        disabled={isUploading}
      >
        {isUploading ? "Uploading..." : "Save Changes"}
      </button>
    </div>
  );
};

export default ShortMedia;

/* -------------------- Styles -------------------- */
const styles: { [key: string]: React.CSSProperties } = {

  heading: {
    fontSize: 22,
    fontWeight: 700,
    textAlign: "center",
    color: softGold,
  },
  tip: {
    textAlign: "center",
    color: "#b3a77a",
    fontSize: 14,
    marginBottom: 30,
  },
  mediaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 24,
    justifyContent: "center",
  },
  card: {
    background: "#ffffff",
    borderRadius: 16,
    padding: 20,
    flex: "1 1 420px",
    border: borderGold,
    boxShadow: "0 0 20px rgba(198,165,92,0.1)",
  },
  subheading: {
    color: softGold,
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 10,
  },
  uploadBox: {
    border: `2px dashed rgba(198,165,92,0.5)`,
    borderRadius: 14,
    padding: "40px 10px",
    textAlign: "center",
    cursor: "pointer",
    color: "#b3a77a",
    marginBottom: 16,
    transition: "all 0.3s ease",
  },
  gallery: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
  },
  thumbWrapper: { position: "relative" },
  thumb: {
    width: 100,
    height: 100,
    objectFit: "cover",
    borderRadius: 12,
    border: `2px solid ${softGold}`,
  },
  trash: {
    position: "absolute",
    top: 4,
    right: 4,
    background: "rgba(0,0,0,0.7)",
    borderRadius: "50%",
    padding: "3px 6px",
    cursor: "pointer",
    fontSize: 13,
  },
  video: {
    width: "100%",
    borderRadius: 14,
    border: `2px solid ${softGold}`,
    marginTop: 12,
  },
  replaceBtn: {
    position: "absolute",
    top: 10,
    left: 10,
    background: softGold,
    border: "none",
    borderRadius: 16,
    padding: "4px 10px",
    color: black,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  uploadBtn: {
    display: "block",
    margin: "40px auto 0",
    background: softGold,
    color: black,
    border: "none",
    borderRadius: 30,
    padding: "10px 28px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 0 25px rgba(198,165,92,0.3)",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    zIndex: 2000,
  },
  successBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    color: softGold,
  },
  errorBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#f44336",
    background: "#fff",
    borderRadius: 16,
    padding: 24,
    maxWidth: 300,
    textAlign: "center",
  },
};
